import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  ImageIcon, Upload, Trash2, Tag, FolderOpen, Search, Grid3X3,
  Check, ArrowRight, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIA_CATEGORIES = [
  "Technologia", "Biznes", "Zdrowie", "Finanse", "Lifestyle",
  "Motoryzacja", "Sport", "Edukacja", "Natura", "Miasto",
  "Jedzenie", "Podroze", "Ludzie", "Abstrakcja", "Inne"
];

export default function MediaLibrary() {
  const { t } = useLanguage();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("all");
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadCat, setUploadCat] = useState("Inne");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [moveCat, setMoveCat] = useState("");
  const [moving, setMoving] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [newCatDialog, setNewCatDialog] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const params = filterCat !== "all" ? `?category=${filterCat}` : "";
      const [mediaRes, catsRes] = await Promise.all([
        axios.get(`${API}/media${params}`),
        axios.get(`${API}/media/categories`),
      ]);
      setMedia(mediaRes.data);
      setAllCategories(catsRes.data.categories || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterCat]);

  useEffect(() => { fetchData(); setSelected([]); }, [fetchData]);

  const handleBulkUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    try {
      const filesData = [];
      for (const file of uploadFiles) {
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        filesData.push({ filename: file.name, data_url: dataUrl });
      }
      await axios.post(`${API}/media/bulk-upload`, { files: filesData, category: uploadCat });
      toast.success(`Dodano ${filesData.length} zdjec do kategorii ${uploadCat}`);
      setUploadDialog(false);
      setUploadFiles([]);
      fetchData();
    } catch (err) { toast.error("Blad uploadu"); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`);
      toast.success("Usunieto");
      fetchData();
    } catch (err) { toast.error("Blad usuwania"); }
  };

  const handleBulkDelete = async () => {
    for (const id of selected) {
      await axios.delete(`${API}/media/${id}`).catch(() => {});
    }
    toast.success(`Usunieto ${selected.length} zdjec`);
    setSelected([]);
    fetchData();
  };

  const handleBulkMove = async () => {
    if (!moveCat || selected.length === 0) return;
    setMoving(true);
    try {
      const res = await axios.patch(`${API}/media/bulk-move`, { media_ids: selected, category: moveCat });
      toast.success(`Przeniesiono ${res.data.moved} zdjec do ${moveCat}`);
      setSelected([]);
      setMoveCat("");
      fetchData();
    } catch (err) { toast.error("Blad przenoszenia"); }
    finally { setMoving(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelected(media.map(m => m.id));
  const deselectAll = () => setSelected([]);

  const handleFileSelect = (e) => setUploadFiles(Array.from(e.target.files));

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${API}/media/categories`, { name: newCatName.trim() });
      toast.success(`Kategoria "${newCatName.trim()}" dodana`);
      setNewCatName("");
      setNewCatDialog(false);
      fetchData();
    } catch (err) { toast.error("Blad dodawania kategorii"); }
  };

  const categoriesList = allCategories.length > 0 ? allCategories : MEDIA_CATEGORIES;

  // Zlicz kategorie
  const catCounts = {};
  // Nie mamy tego z backendu w tym ujeciu, ale mozemy policzyc z media
  media.forEach(m => { catCounts[m.category] = (catCounts[m.category] || 0) + 1; });

  return (
    <div data-testid="media-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("media.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("media.subtitle")}</p>
        </div>
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogTrigger asChild>
            <Button data-testid="upload-media-btn" className="rounded-lg gap-2">
              <Upload className="w-4 h-4" strokeWidth={1.5} /> {t("media.bulkUpload")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{t("media.bulkUpload")}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-xs">{t("media.selectCategory")}</Label>
                <Select value={uploadCat} onValueChange={setUploadCat}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoriesList.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{t("media.selectFiles")}</Label>
                <Input type="file" multiple accept="image/*" onChange={handleFileSelect} className="text-xs" />
                {uploadFiles.length > 0 && (
                  <p className="text-[10px] text-muted-foreground">{uploadFiles.length} {t("media.filesSelected")}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialog(false)}>{t("sites.cancel")}</Button>
              <Button onClick={handleBulkUpload} disabled={uploading || !uploadFiles.length}>
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                {t("media.upload")} ({uploadFiles.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Kategorie */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilterCat("all")}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            filterCat === "all" ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-accent"
          }`}>
          Wszystkie ({media.length})
        </button>
        {categoriesList.map(c => (
          <button key={c} onClick={() => setFilterCat(filterCat === c ? "all" : c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              filterCat === c ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-accent"
            }`}>
            {c}
          </button>
        ))}
        {/* Dodaj kategorie */}
        <Dialog open={newCatDialog} onOpenChange={setNewCatDialog}>
          <DialogTrigger asChild>
            <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-muted text-muted-foreground hover:bg-blue-100 hover:text-blue-600 transition-all border border-dashed border-slate-300">
              + Nowa kategoria
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle className="font-heading">Dodaj nowa kategorie</DialogTitle></DialogHeader>
            <div className="py-4 space-y-3">
              <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                placeholder="Nazwa kategorii (np. Kosmetyki)" className="text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddCategory()} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewCatDialog(false)}>Anuluj</Button>
              <Button onClick={handleAddCategory} disabled={!newCatName.trim()}>Dodaj</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Akcje masowe */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
          <Badge className="bg-primary text-white">{selected.length} zaznaczonych</Badge>

          {/* Przenies do kategorii */}
          <Select value={moveCat} onValueChange={setMoveCat}>
            <SelectTrigger className="h-8 w-[160px] text-xs"><SelectValue placeholder="Przenies do..." /></SelectTrigger>
            <SelectContent>
              {categoriesList.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8 text-xs gap-1" onClick={handleBulkMove} disabled={!moveCat || moving}>
            {moving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
            Przenies
          </Button>

          <Button variant="outline" size="sm" className="h-8 text-xs text-destructive gap-1 ml-auto" onClick={handleBulkDelete}>
            <Trash2 className="w-3 h-3" /> Usun zaznaczone
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={deselectAll}>Odznacz</Button>
        </div>
      )}

      {/* Grid zdjec */}
      {media.length === 0 ? (
        <Card className="rounded-xl border shadow-sm bg-card">
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <ImageIcon className="w-12 h-12 mb-3 opacity-20" strokeWidth={1} />
            <p className="text-xs">{t("media.noMedia")}</p>
            <p className="text-[10px] mt-1">{t("media.uploadHint")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={selectAll}>Zaznacz wszystkie</Button>
            {selected.length > 0 && <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={deselectAll}>Odznacz</Button>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {media.map((img) => {
              const isSelected = selected.includes(img.id);
              return (
                <Card key={img.id || img.filename}
                  className={`rounded-xl border shadow-sm hover:shadow-md transition-all group overflow-hidden cursor-pointer ${
                    isSelected ? "ring-2 ring-primary border-primary" : "bg-card"
                  }`}
                  onClick={() => toggleSelect(img.id)}>
                  <div className="aspect-[3/2] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 relative overflow-hidden">
                    {img.url ? (
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover relative z-[1]"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-slate-300 dark:text-slate-600" strokeWidth={1} />
                    </div>
                    {/* Checkbox */}
                    <div className={`absolute top-1.5 left-1.5 w-5 h-5 rounded border-2 flex items-center justify-center z-10 transition-all ${
                      isSelected ? "bg-primary border-primary" : "border-white/70 bg-black/20 opacity-0 group-hover:opacity-100"
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    {/* Delete */}
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(img.id); }}
                      className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 text-white rounded-md p-0.5 z-10">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <CardContent className="p-2">
                    <p className="text-[10px] font-medium text-foreground truncate">{img.filename}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Badge variant="outline" className="text-[7px] rounded-sm">{img.category}</Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
